import { createResource, type ResourceReturn, type ResourceSource } from "solid-js"
import { datoxApiBaseUrl } from "@/datox/api"
import { activeCompanyID } from "@/datox/local-auth"

// One read cache for the V2 pages (Artyom: «так долго все грузит и открывает»; DESIGN-LOG §67). What a page read is
// kept, so a page visited again shows it at once and reads it again quietly (stale-while-revalidate), and two pages or
// parts asking for the same list at the same time make one request. The rules:
//  - Every key is the company's and the signed-in person's. A company switch (here or in another tab) or a sign-out
//    never shows another company's lists, not even for a frame: the key no longer matches, and the cache empties.
//  - Any write to the API (any method but GET, HEAD or OPTIONS, from any page, dialog or panel, Bobur's included)
//    empties it when it starts and again when it ends, and a read on its way meanwhile is not kept: nobody sees a list
//    from before their own action. The page that wrote reads again as it always did.
//  - Polls read through it (fresh), so what they bring is what the next visit shows.
// The API's calls are unchanged: the pages pass them in (reads.ts names the shared ones).

// One list: its key (without the company and person, added here) and the call that reads it.
export type Read<T> = { key: string; read: () => Promise<T> }

type Entry = { kept?: { value: unknown }; read?: Promise<unknown> }
const entries = new Map<string, Entry>()
// Moves on every write and every clear: a quiet read that started before it is not drawn.
let era = 0
let reader = ""

const scoped = (key: string) => `${activeCompanyID()}\n${reader}\n${key}`

// Empty it: a write, a sign-out, another person or company.
export function clearReads() {
  era += 1
  entries.clear()
}

// Who reads (the signed-in account, set by the sign-in): another person starts from an empty cache.
export function setReader(id: string) {
  if (id === reader) return
  reader = id
  clearReads()
}

// What is kept for a list, if anything (never a read on its way).
export function keptRead<T>(item: Read<T>): T | undefined {
  watchWrites()
  return entries.get(scoped(item.key))?.kept?.value as T | undefined
}

// Read a list: what is kept (unless `fresh`), else the read already on its way for it, else a new one. Its answer is
// kept only if it is still the key's read: a write (or forgetRead) while it was on its way drops it, as its data may
// be from before. A failed read keeps nothing new and leaves what was kept.
export function readKept<T>(item: Read<T>, fresh = false): Promise<T> {
  watchWrites()
  const id = scoped(item.key)
  const entry = entries.get(id)
  if (!fresh && entry?.kept) return Promise.resolve(entry.kept.value as T)
  if (entry?.read) return entry.read as Promise<T>
  const read: Promise<T> = item.read().then(
    (value) => {
      if (entries.get(id)?.read === read) entries.set(id, { kept: { value } })
      return value
    },
    (error: unknown) => {
      const now = entries.get(id)
      if (now?.read === read) entries.set(id, { kept: now.kept })
      throw error
    },
  )
  entries.set(id, { kept: entry?.kept, read })
  return read
}

// A list that changed under the page (an update of it ended): its next look reads it, as before the cache.
export function forgetRead(item: Read<unknown>) {
  entries.delete(scoped(item.key))
}

// createResource through the cache. `kept(source)` is what is kept for it, whole, or undefined; `read(source)` reads it
// anew through readKept(…, true) (it runs only when nothing is kept, on refetch, and for the quiet read). When it is kept
// the resource is ready with it at once (never pending, so no skeleton and no Suspense), then reads it again quietly;
// the answer replaces it unless it says the same, a newer read came first or a write happened meanwhile, and a failed
// quiet read keeps it. When it is not kept the resource reads as createResource does. `settled(source)`: a record that
// no longer changes (a completed update's) is not read again.
export function createKeptResource<T, S>(source: ResourceSource<S>, kept: (source: S) => T | undefined, read: (source: S) => Promise<T>, options?: { settled?: (source: S) => boolean }): ResourceReturn<T> {
  let latest = 0
  const resource: ResourceReturn<T> = createResource(source, (value, info) => {
    const call = ++latest
    const now = info.refetching ? undefined : kept(value)
    if (now === undefined) return read(value)
    if (options?.settled?.(value)) return now
    const at = era
    read(value).then((next) => {
      if (call !== latest || at !== era || sameValue(next, resource[0].latest)) return
      resource[1].mutate(() => next)
    }, () => undefined)
    return now
  })
  return resource
}

// One list as a resource; `recover` stands in for a failed read, as the page's own .catch did.
export function createKeptRead<T>(item: Read<T>, recover?: (error: unknown) => T): ResourceReturn<T> {
  return createKeptResource(() => true, () => keptRead(item), () => (recover ? readKept(item, true).catch(recover) : readKept(item, true)))
}

// The same data, whatever objects carry it (Maps and Sets by their entries).
function sameValue(a: unknown, b: unknown) {
  if (a === b) return true
  const text = (value: unknown) => JSON.stringify(value, (_, item: unknown) => (item instanceof Map || item instanceof Set ? [...item] : item))
  return text(a) === text(b)
}

// ---------- writes empty it ----------

const SAFE = ["GET", "HEAD", "OPTIONS"]
// The keys a company switch or a sign-in change in another tab writes.
const WHO = ["activeCompanyId", "datox.agent.companyId", "datox.agent.userId", "datox.agent.userEmail"]
let watching = false

// Every request to the API that is not a read empties the cache when it starts and when it ends. On fetch itself, so a
// write from any module (api.ts, workspaces.ts, automations.ts, his panels) counts without each one remembering to.
// Installed once, with the first cached read; `target` lets the tests stand in for the window.
export function watchWrites(target?: Pick<Window, "fetch">) {
  if (!target) {
    if (watching || typeof window === "undefined") return
    watching = true
    window.addEventListener("storage", (event) => { if (event.key === null || WHO.includes(event.key)) clearReads() })
  }
  const host = target ?? window
  const fetch = host.fetch.bind(host)
  host.fetch = (input, init) => {
    if (!writes(input, init)) return fetch(input, init)
    clearReads()
    return fetch(input, init).finally(clearReads)
  }
}

function writes(input: RequestInfo | URL, init?: RequestInit) {
  const method = (init?.method ?? (input instanceof Request ? input.method : "GET")).toUpperCase()
  if (SAFE.includes(method)) return false
  const base = typeof location === "undefined" ? "http://localhost/" : location.href
  const url = new URL(typeof input === "string" ? input : input instanceof URL ? input.href : input.url, base)
  return url.href.startsWith(new URL(`${datoxApiBaseUrl()}/api/`, base).href)
}
